import { useGetCurrencySymbol } from 'app/appState';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { WarehouseForBooking } from 'entities/Warehouse';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { Box, CachedBox } from '../../model/types';
import { getBoxDiscountedPriceAmount } from '../helpers/getBoxDiscountedPriceAmount';
import { parseDiscountData } from '../helpers/parseDiscountData';
import { FIRST_MONTH_DISCOUNT_REGEX } from 'shared/utils/regex';
import { getBoxPriceAmountWithVat } from '../helpers/getBoxPriceAmountWithVat';
import { UserAccountType } from 'entities/User';
import { UnitType } from 'entities/Contract';
import { formatPrice } from 'shared/utils/helpers/formatPrice';

export const useGetBoxDiscountedPriceString = ({
  warehouseInfo,
  boxInfo,
  showPriceWithVat,
  accountType,
  unitType = UnitType.BOX,
}: {
  warehouseInfo: Nullable<WarehouseForBooking> | undefined;
  boxInfo: Nullable<Box> | Nullable<CachedBox>;
  showPriceWithVat?: boolean;
  accountType?: Nullable<UserAccountType>;
  unitType?: Nullable<UnitType>;
}): string | undefined => {
  const { t } = useAppTranslation('booking');

  const currencySymbol = useGetCurrencySymbol();

  const warehouseDiscounts = warehouseInfo?.warehouseDiscounts;

  const firstMonthDiscounts = warehouseDiscounts
    ?.filter(
      (discount) => discount.unitType === unitType && discount.discounts.split(',').some((d) => FIRST_MONTH_DISCOUNT_REGEX.test(d.trim())),
    )
    .sort((a, b) => a.priority - b.priority);

  let actualDiscount = firstMonthDiscounts?.find((discount) => !discount.unitSizes) || firstMonthDiscounts?.[0];

  if (boxInfo?.sizeCode.square) {
    actualDiscount = firstMonthDiscounts?.find((discount) => {
      const unitSizes = discount.unitSizes?.split(',').map((size) => Number(size));

      return unitSizes?.includes(boxInfo.sizeCode.square);
    });
  }

  const discountData = parseDiscountData(actualDiscount);

  if (!actualDiscount || !discountData) {
    return undefined;
  }

  const getRatePrice = (): number | undefined => {
    if (boxInfo?.monthRate) {
      return boxInfo.monthRate * boxInfo.priceFactor;
    }
    if (boxInfo?.weekRate) {
      return boxInfo.weekRate * boxInfo.priceFactor;
    }
    if (boxInfo?.dailyRate) {
      return boxInfo.dailyRate * boxInfo.priceFactor;
    }
  };

  const discountedBoxPriceAmount = getBoxDiscountedPriceAmount({
    warehouseDiscounts,
    boxPrice: getRatePrice(),
    boxSquare: boxInfo?.sizeCode.square,
  });

  if (!discountedBoxPriceAmount) {
    return undefined;
  }

  const boxPriceAmountWithVat = getBoxPriceAmountWithVat(discountedBoxPriceAmount, warehouseInfo, accountType);

  const discountedPrice = showPriceWithVat ? boxPriceAmountWithVat : discountedBoxPriceAmount;

  if (boxInfo?.monthRate) {
    return discountData.allDurationEqual
      ? `${formatPrice(roundNumber(discountedPrice))} ${currencySymbol} ${t('for {{discountDuration}} month(s)', {
          discountDuration: t(discountData.duration),
        })}`
      : `${formatPrice(roundNumber(discountedPrice))} ${currencySymbol}/${t('month')}`;
  }

  if (boxInfo?.weekRate) {
    return discountData.allDurationEqual
      ? `${formatPrice(roundNumber(discountedPrice))} ${currencySymbol} ${t('for {{discountDuration}} week(s)', {
          discountDuration: t(discountData.duration),
        })}`
      : `${formatPrice(roundNumber(discountedPrice))} ${currencySymbol}/${t('week')}`;
  }

  if (boxInfo?.dailyRate) {
    return discountData.allDurationEqual
      ? `${formatPrice(roundNumber(discountedPrice))} ${currencySymbol} ${t('for {{discountDuration}} day(s)', {
          discountDuration: t(discountData.duration),
        })}`
      : `${formatPrice(roundNumber(discountedPrice))} ${currencySymbol}/${t('day')}`;
  }
};
